import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';

import { type LogoSize, ProductLogo } from './ProductLogos.tsx';

const ICON_SIZE: Record<'small' | 'medium' | 'large', LogoSize> = {
	small: 'xxsmall',
	medium: 'xsmall',
	large: 'small',
};

const i18n = defineMessages({
	googleDriveIconAlt: {
		id: 'space-curation.space-navigation.google-drive-icon.alt',
		defaultMessage: 'Google Drive icon',
		description: 'Alt text for the Google Drive logo displayed next to the third-party auth prompt',
	},
	onedriveIconAlt: {
		id: 'space-curation.space-navigation.onedrive-icon.alt',
		defaultMessage: 'OneDrive icon',
		description: 'Alt text for the OneDrive logo displayed next to the third-party auth prompt',
	},
	loomIconAlt: {
		id: 'space-curation.space-navigation.loom-icon.alt',
		defaultMessage: 'Loom icon',
		description: 'Alt text for the Loom logo displayed next to the third-party auth prompt',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		marginInlineEnd: token('space.100'),
		verticalAlign: 'top',
	},
	iconSlotFirst: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
		zIndex: 100,
		transform: 'rotate(-8deg)',
	},
	iconSlotSingle: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
	},
	iconSlotSecond: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.050'),
		position: 'relative',
		zIndex: 1,
		transform: 'rotate(8deg)',
	},
	iconSlotThird: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.050'),
		position: 'relative',
		zIndex: 0,
	},
});

export type IconKey = 'google-drive' | 'onedrive' | 'loom';

const ICON_ALT_KEY: Record<IconKey, keyof typeof i18n> = {
	'google-drive': 'googleDriveIconAlt',
	onedrive: 'onedriveIconAlt',
	loom: 'loomIconAlt',
};

const SLOT_STYLES = [styles.iconSlotFirst, styles.iconSlotSecond, styles.iconSlotThird] as const;

type StackedContentIconsProps = {
	variant?: 'small' | 'medium' | 'large';
	testId?: string;
	singleIcon?: IconKey;
	/**
	 * Render this node in the single-icon slot instead of a local icon. Takes precedence
	 * over `singleIcon` — used to render a config-supplied connector logo.
	 */
	singleIconNode?: React.ReactNode;
	/** Show 2–3 stacked icons in order (first is tilted left, second right, third flat). Mutually exclusive with `singleIcon`. */
	icons?: [IconKey, IconKey] | [IconKey, IconKey, IconKey];
};

export const StackedContentIcons = ({
	variant = 'small',
	testId = 'stacked-content-icons',
	singleIcon,
	singleIconNode,
	icons,
}: StackedContentIconsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const size = ICON_SIZE[variant];

	if (singleIconNode) {
		return (
			<Box xcss={styles.wrapper} testId={testId}>
				<Box xcss={styles.iconSlotSingle}>{singleIconNode}</Box>
			</Box>
		);
	}

	if (singleIcon) {
		return (
			<Box xcss={styles.wrapper} testId={testId}>
				<Box xcss={styles.iconSlotSingle}>
					<ProductLogo
						logoKey={singleIcon}
						size={size}
						label={formatMessage(i18n[ICON_ALT_KEY[singleIcon]])}
					/>
				</Box>
			</Box>
		);
	}

	const iconList = icons ?? (['google-drive', 'onedrive'] as [IconKey, IconKey]);

	return (
		<Box xcss={styles.wrapper} testId={testId}>
			{iconList.map((key, index) => (
				<Box key={key} xcss={cx(SLOT_STYLES[index])}>
					<ProductLogo logoKey={key} size={size} label={formatMessage(i18n[ICON_ALT_KEY[key]])} />
				</Box>
			))}
		</Box>
	);
};
